import { movedPath, parentFolder, withFolderExpanded } from "./skillFilePaths";

/**
 * The file tree editor as one state machine, shared by a skill still being
 * created (files held locally) and a saved one (files on the server).
 */
export type SkillFileEditorState = {
  /** Which tree this state belongs to; a new key starts over. */
  sessionKey: string;
  selectedPath: string | null;
  draft: string;
  /** What the store holds for `selectedPath`; the draft is dirty when it differs. */
  savedContent: string;
  /** The latest open; a load answered for any other id is stale and dropped. */
  requestId: number;
  loadingContent: boolean;
  pending: "write" | "delete" | "move" | null;
  expandedFolders: ReadonlySet<string>;
  /** Folders expanded and the first file opened for this tree. */
  initialized: boolean;
};

export type SkillFileEditorAction =
  | { type: "reset"; sessionKey: string }
  | { type: "initialized"; folders: readonly string[] }
  | { type: "openStarted"; path: string; requestId: number }
  | { type: "openLoaded"; requestId: number; content: string }
  | { type: "openFailed"; requestId: number }
  | { type: "fileCreated"; path: string; content: string; requestId: number }
  | { type: "cleared"; requestId: number }
  | { type: "edited"; draft: string }
  | { type: "pending"; pending: SkillFileEditorState["pending"] }
  | { type: "saved"; path: string; content: string }
  | { type: "moved"; from: string; to: string }
  | { type: "deleted"; path: string }
  | { type: "folderToggled"; path: string }
  | { type: "folderExpanded"; folder: string };

export function initialSkillFileEditorState(sessionKey: string): SkillFileEditorState {
  return {
    sessionKey,
    selectedPath: null,
    draft: "",
    savedContent: "",
    requestId: 0,
    loadingContent: false,
    pending: null,
    expandedFolders: new Set(),
    initialized: false,
  };
}

export function isSkillFileDraftDirty(state: SkillFileEditorState): boolean {
  return state.draft !== state.savedContent;
}

function toggled(current: ReadonlySet<string>, path: string): Set<string> {
  const next = new Set(current);
  if (next.has(path)) {
    next.delete(path);
  } else {
    next.add(path);
  }
  return next;
}

function withBuffer(
  state: SkillFileEditorState,
  selectedPath: string | null,
  content: string,
  requestId: number
): SkillFileEditorState {
  return {
    ...state,
    selectedPath,
    draft: content,
    savedContent: content,
    requestId,
    loadingContent: false,
  };
}

/** Actions answering an earlier open, which a later open or clear makes stale. */
function reduceOpenResult(
  state: SkillFileEditorState,
  action: Extract<SkillFileEditorAction, { type: "openLoaded" | "openFailed" }>
): SkillFileEditorState {
  if (action.requestId !== state.requestId) {
    return state;
  }
  if (action.type === "openFailed") {
    // Never leave a file open with a blank buffer that Save would write over it.
    return withBuffer(state, null, "", state.requestId);
  }
  return withBuffer(state, state.selectedPath, action.content, state.requestId);
}

function reduceTreeChange(
  state: SkillFileEditorState,
  action: Extract<SkillFileEditorAction, { type: "saved" | "moved" | "deleted" }>
): SkillFileEditorState {
  switch (action.type) {
    case "saved":
      return action.path === state.selectedPath
        ? { ...state, savedContent: action.content, pending: null }
        : { ...state, pending: null };
    case "moved":
      return {
        ...state,
        pending: null,
        selectedPath: state.selectedPath
          ? (movedPath(state.selectedPath, action.from, action.to) ?? state.selectedPath)
          : null,
        expandedFolders: withFolderExpanded(parentFolder(action.to), state.expandedFolders),
      };
    case "deleted":
      return action.path === state.selectedPath
        ? { ...withBuffer(state, null, "", state.requestId + 1), pending: null }
        : { ...state, pending: null };
  }
}

export function skillFileEditorReducer(
  state: SkillFileEditorState,
  action: SkillFileEditorAction
): SkillFileEditorState {
  switch (action.type) {
    case "reset":
      return initialSkillFileEditorState(action.sessionKey);
    case "initialized":
      return { ...state, initialized: true, expandedFolders: new Set(action.folders) };
    case "openStarted":
      return {
        ...withBuffer(state, action.path, "", action.requestId),
        loadingContent: true,
      };
    case "openLoaded":
    case "openFailed":
      return reduceOpenResult(state, action);
    case "fileCreated":
      return {
        ...withBuffer(state, action.path, action.content, action.requestId),
        pending: null,
        expandedFolders: withFolderExpanded(parentFolder(action.path), state.expandedFolders),
      };
    case "cleared":
      return withBuffer(state, null, "", action.requestId);
    case "edited":
      return { ...state, draft: action.draft };
    case "pending":
      return { ...state, pending: action.pending };
    case "saved":
    case "moved":
    case "deleted":
      return reduceTreeChange(state, action);
    case "folderToggled":
      return { ...state, expandedFolders: toggled(state.expandedFolders, action.path) };
    case "folderExpanded":
      return {
        ...state,
        expandedFolders: withFolderExpanded(action.folder, state.expandedFolders),
      };
  }
}
